import * as Dialog from '@radix-ui/react-dialog'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { cn } from '@/design-system'
import { useUi } from '@/stores/ui'
import { preloadPath } from '@/app/pageRegistry'
import { flattenPages } from '@/navigation/tree'
import { useVisibleNav } from '@/navigation/useNavigation'
import { lookupService } from '@/services/lookupService'
import { staffPushService } from '@/services/staffPushService'
import { CommandPalette } from './CommandPalette'
import { NetworkBanner, UpdatePrompt } from './SystemBanners'
import { PushPrompt } from './PushPrompt'
import { InstallBanner } from './AppInstall'
import { MobileTabBar } from './MobileTabBar'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'
import { TabPanes, useWorkspaceSync } from './workspace/Workspace'
import { TabStrip } from './workspace/TabStrip'

export function AppShell() {
  const collapsed = useUi((s) => s.sidebarCollapsed)
  const mobileOpen = useUi((s) => s.mobileNavOpen)
  const setMobileNav = useUi((s) => s.setMobileNav)
  const [commandOpen, setCommandOpen] = useState(false)
  const fetching = useIsFetching()
  const [busy, setBusy] = useState(false)
  const tree = useVisibleNav()
  const qc = useQueryClient()

  // the phone menu closed by a tap on a page: the body never stays locked behind it (a page that would not take a tap)
  useEffect(() => {
    if (mobileOpen) return
    const t = setTimeout(() => { if (document.body.style.pointerEvents === 'none') document.body.style.pointerEvents = '' }, 350)
    return () => clearTimeout(t)
  }, [mobileOpen])

  // workspace tabs: the router and the open tabs stay in step (scroll per tab is handled by TabPanes)
  useWorkspaceSync()

  // a device with alerts switched on is re-confirmed on every open: who it belongs to and what he may read today
  useEffect(() => { void staffPushService.refresh().catch(() => undefined) }, [])

  // Only show the progress bar for requests slower than 250ms (no flicker on cached data).
  useEffect(() => {
    if (!fetching) return setBusy(false)
    const t = setTimeout(() => setBusy(true), 250)
    return () => clearTimeout(t)
  }, [fetching])

  // Warm up while the user is idle: download every permitted screen and the shared reference lists.
  useEffect(() => {
    const idle = (cb: () => void) => ('requestIdleCallback' in window ? (window as Window & { requestIdleCallback: (f: () => void) => number }).requestIdleCallback(cb) : setTimeout(cb, 1200))
    idle(() => {
      for (const key of ['statuses', 'branches', 'runners', 'cities', 'clients', 'reasons'] as const) {
        void qc.prefetchQuery({ queryKey: ['lookup', key], queryFn: ({ signal }) => (lookupService[key] as (s?: AbortSignal) => Promise<unknown>)(signal), staleTime: 60 * 60_000 })
      }
    })
    const paths = [...new Set(flattenPages(tree).map((p) => p.page.path))]
    let i = 0
    const step = () => {
      for (const p of paths.slice(i, i + 4)) preloadPath(p)
      i += 4
      if (i < paths.length) idle(step)
    }
    const t = setTimeout(() => idle(step), 2500)
    return () => clearTimeout(t)
  }, [tree, qc])

  return (
    <div className="flex h-dvh overflow-hidden">
      {/* a column beside the page from 768px, as on the portal — it never covers the page (owner, 2026-10-20) */}
      <aside className={cn('hidden shrink-0 py-3 ps-3 transition-[width] duration-200 md:block', collapsed ? 'w-[88px]' : 'w-[284px]')}>
        <Sidebar variant="desktop" />
      </aside>

      <Dialog.Root open={mobileOpen} onOpenChange={setMobileNav}>
        <Dialog.Portal>
          {/* a wide drawer (long Arabic names fit) over a scrim that says the page is behind it */}
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/35 backdrop-blur-[1.5px] data-[state=open]:animate-fade-in md:hidden" />
          <Dialog.Content className="fixed inset-y-0 start-0 z-50 w-[88vw] max-w-[380px] shadow-lg data-[state=open]:animate-fade-in md:hidden">
            <Dialog.Title className="sr-only">Navigation</Dialog.Title>
            <Dialog.Description className="sr-only">Main navigation</Dialog.Description>
            <Sidebar variant="mobile" />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onOpenCommand={() => setCommandOpen(true)} />
        <TabStrip onOpenCommand={() => setCommandOpen(true)} />
        <NetworkBanner />
        <PushPrompt />
        {busy && <div className="top-progress" role="progressbar" aria-label="Loading" />}
        <main id="main" className="min-h-0 flex-1 overflow-y-auto">
          {/* every open tab stays mounted (keep-alive); only the front one is shown — see workspace/Workspace.tsx */}
          <TabPanes />
        </main>
      </div>
      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
      <UpdatePrompt />
      <MobileTabBar />
      <InstallBanner />
    </div>
  )
}
